import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Tekstualni izbor koji živi u adresi, ne u stanju komponente.
 *
 * Blizanac `usePage`, i postoji iz istog razloga. Strana u adresi sama po sebi ne vraća korisnika
 * tamo gde je bio: spisak koji ima tabove drži u adresi broj strane a tab u `useState`, pa povratak
 * iz jednog reda vrati stranu i **izgubi tab** — korisnik osvane na sedmoj strani pogrešnog spiska,
 * što je gore od vraćanja na prvu, jer izgleda kao da se ništa nije promenilo.
 *
 * Mesto u spisku je par (tab, strana) i oba pripadaju adresi. Usput se i veza može poslati nekome
 * takva kakva je.
 *
 * Kao i kod strane, izmena ne upisuje novu stavku u istoriju (`replace`): prelazak kroz tabove ne
 * sme da znači pet pritisaka na „nazad" da bi se izašlo sa spiska.
 *
 * Podrazumevana vrednost se u adresu ne piše — `?tab=customers` je šum koji ništa ne kaže.
 *
 * **Jedan upis po potezu.** `setSearchParams` iz react-router-a 7 gradi novu adresu iz parametara
 * TEKUĆEG iscrtavanja (`nextInit(new URLSearchParams(searchParams))`, gde `searchParams` visi o
 * `location.search`). Dva poziva u istom potezu zato čitaju istu osnovu i **drugi pregazi prvi**.
 * Zbog toga ovaj hook i prima `ponisti` umesto da se zove dvaput, i zbog toga više izbora koji se
 * menjaju zajedno (brisanje svih filtera) traži JEDAN upisivač, a ne po jedan hook za svaki.
 *
 * @param ponisti parametri koje izmena ovog izbora obesmišljava, brišu se u ISTOM upisu. Strana
 *   drugog taba nije strana ovog, pa dva odvojena upisa znače jedan zahtev za stranom koja se
 *   odmah odbacuje i treptaj pogrešnog spiska između njih.
 */
export function useUrlParam<T extends string>(
  kljuc: string,
  podrazumevano: T,
  dozvoljene: readonly T[],
  ponisti: readonly string[] = [],
): [T, (v: T) => void] {
  const [params, setParams] = useSearchParams();
  const sirovo = params.get(kljuc);
  // Adresu piše i čovek, pa vrednost van spiska nije stanje nego greška u unosu: pada na
  // podrazumevanu, tiho, jer ekran ne sme da ostane bez taba.
  const vrednost = dozvoljene.includes(sirovo as T) ? (sirovo as T) : podrazumevano;

  const kljucevi = ponisti.join(",");
  const postavi = useCallback(
    (v: T) => {
      setParams(
        (prethodni) => {
          const sledeci = new URLSearchParams(prethodni);
          if (v === podrazumevano) sledeci.delete(kljuc);
          else sledeci.set(kljuc, v);
          for (const k of kljucevi === "" ? [] : kljucevi.split(",")) sledeci.delete(k);
          return sledeci;
        },
        { replace: true },
      );
    },
    [kljuc, kljucevi, podrazumevano, setParams],
  );

  return [vrednost, postavi];
}
